import { QueryClient, QueryKey } from "@tanstack/react-query";

type ReservationInvalidationTarget = {
  id?: number | null;
  entrada?: string | null;
  salida?: string | null;
  [key: string]: unknown;
};

type MonthCalendarReservationsData = {
  reservas?: ReservationInvalidationTarget[];
  [key: string]: unknown;
};

export const markLocalReservationInvalidation = (queryClient: QueryClient) => {
  queryClient.setQueryData(["last-local-invalidation"], Date.now());
};

export const beginReservationInvalidation = (queryClient: QueryClient) => {
  queryClient.setQueryData(["is-invalidating"], true);
};

export const endReservationInvalidation = (queryClient: QueryClient) => {
  queryClient.setQueryData(["is-invalidating"], false);
};

export const markRecentlyDeletedReservation = (queryClient: QueryClient) => {
  queryClient.setQueryData(["recently-deleted"], Date.now());
};

export const clearRecentlyDeletedReservation = (queryClient: QueryClient) => {
  queryClient.setQueryData(["recently-deleted"], false);
};

const parseReservationDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;

  return new Date(year, month - 1, day);
};

const getMonthCalendarQueryRange = (
  queryKey: QueryKey
): { start: Date; end: Date } | null => {
  const [, year, month] = queryKey;
  if (typeof year !== "number" || typeof month !== "number") return null;

  return {
    start: new Date(year, month - 2, 1),
    end: new Date(year, month + 1, 0),
  };
};

const reservationOverlapsRange = (
  reservation: ReservationInvalidationTarget,
  range: { start: Date; end: Date }
): boolean => {
  const entrada = parseReservationDate(reservation.entrada);
  const salida = parseReservationDate(reservation.salida) || entrada;
  if (!entrada || !salida) return false;

  const firstDate = entrada <= salida ? entrada : salida;
  const lastDate = entrada <= salida ? salida : entrada;
  return firstDate <= range.end && lastDate >= range.start;
};

export const applyReservationToMonthCalendarData = <
  TData extends MonthCalendarReservationsData | undefined
>(
  oldData: TData,
  queryKey: QueryKey,
  reservation: ReservationInvalidationTarget
): TData => {
  if (!oldData || !reservation.id || !Array.isArray(oldData.reservas)) {
    return oldData;
  }

  const range = getMonthCalendarQueryRange(queryKey);
  if (!range) return oldData;

  const belongsInQuery = reservationOverlapsRange(reservation, range);
  const currentIndex = oldData.reservas.findIndex(
    (r) => r.id === reservation.id
  );

  if (!belongsInQuery && currentIndex === -1) return oldData;

  const nextReservas = [...oldData.reservas];
  if (!belongsInQuery) {
    nextReservas.splice(currentIndex, 1);
  } else if (currentIndex === -1) {
    nextReservas.push(reservation);
  } else {
    nextReservas[currentIndex] = reservation;
  }

  return { ...oldData, reservas: nextReservas } as TData;
};

export const removeReservationFromMonthCalendarData = <
  TData extends MonthCalendarReservationsData | undefined
>(
  oldData: TData,
  reservationId: number
): TData => {
  if (!oldData || !Array.isArray(oldData.reservas)) return oldData;

  const nextReservas = oldData.reservas.filter((r) => r.id !== reservationId);
  if (nextReservas.length === oldData.reservas.length) return oldData;

  return { ...oldData, reservas: nextReservas } as TData;
};

export const upsertReservationInMonthCalendarCache = (
  queryClient: QueryClient,
  reservation: ReservationInvalidationTarget
): boolean => {
  if (!reservation.id || !reservation.entrada) return false;

  const queries = queryClient.getQueryCache().findAll({
    queryKey: ["month-calendar-reservations"],
  });

  for (const query of queries) {
    queryClient.setQueryData<MonthCalendarReservationsData>(
      query.queryKey,
      (oldData) =>
        applyReservationToMonthCalendarData(
          oldData,
          query.queryKey,
          reservation
        )
    );
  }

  return queries.length > 0;
};

export const removeReservationFromMonthCalendarCache = (
  queryClient: QueryClient,
  reservationId: number
): boolean => {
  const queries = queryClient.getQueryCache().findAll({
    queryKey: ["month-calendar-reservations"],
  });

  for (const query of queries) {
    queryClient.setQueryData<MonthCalendarReservationsData>(
      query.queryKey,
      (oldData) => removeReservationFromMonthCalendarData(oldData, reservationId)
    );
  }

  return queries.length > 0;
};

export const getReservationInvalidationMonthKeys = (
  target: ReservationInvalidationTarget
): string[] => {
  const entrada = parseReservationDate(target.entrada);
  const salida = parseReservationDate(target.salida) || entrada;
  if (!entrada || !salida) return [];

  const firstDate = entrada <= salida ? entrada : salida;
  const lastDate = entrada <= salida ? salida : entrada;
  const current = new Date(firstDate.getFullYear(), firstDate.getMonth() - 1, 1);
  const last = new Date(lastDate.getFullYear(), lastDate.getMonth() + 1, 1);
  const keys: string[] = [];

  while (current <= last) {
    keys.push(`${current.getFullYear()}-${current.getMonth() + 1}`);
    current.setMonth(current.getMonth() + 1);
  }

  return keys;
};

const invalidateMonthCalendarQueries = (
  queryClient: QueryClient,
  targets?: ReservationInvalidationTarget | ReservationInvalidationTarget[]
) => {
  const reservationTargets = targets
    ? Array.isArray(targets)
      ? targets
      : [targets]
    : [];
  const monthKeys = new Set(
    reservationTargets.flatMap(getReservationInvalidationMonthKeys)
  );

  if (monthKeys.size === 0) {
    queryClient.invalidateQueries({ queryKey: ["month-calendar-reservations"] });
    return;
  }

  monthKeys.forEach((key) => {
    const [year, month] = key.split("-").map(Number);
    queryClient.invalidateQueries({
      queryKey: ["month-calendar-reservations", year, month],
    });
  });
};

export const invalidateReservationQueries = (
  queryClient: QueryClient,
  targets?: ReservationInvalidationTarget | ReservationInvalidationTarget[]
) => {
  queryClient.invalidateQueries({ queryKey: ["reservations"] });
  queryClient.invalidateQueries({ queryKey: ["calendar-reservations"] });
  invalidateMonthCalendarQueries(queryClient, targets);
};

export const invalidateParkingReservationQueries = (
  queryClient: QueryClient
) => {
  queryClient.invalidateQueries({ queryKey: ["reservasEstacionamiento"] });
};

export const invalidateParkingConfigQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["estacionamientos"] });
  queryClient.invalidateQueries({ queryKey: ["estacionamientos-config"] });
};

export const invalidateClientQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["clients"] });
};

export const invalidateDepartmentQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["departments"] });
  queryClient.invalidateQueries({ queryKey: ["departments-categories"] });
  queryClient.invalidateQueries({ queryKey: ["departments-zones"] });
};

export const invalidateDepartmentCategoriesQueries = (
  queryClient: QueryClient
) => {
  queryClient.invalidateQueries({ queryKey: ["departments"] });
  queryClient.invalidateQueries({ queryKey: ["departments-categories"] });
};

export const invalidateDepartmentPriceQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["departments"] });
};

export const invalidateStatisticsQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["statistics"], exact: false });
  queryClient.invalidateQueries({
    queryKey: ["statistics-mensual"],
    exact: false,
  });
};
